/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useEffect, useRef } from 'react';
import { Decimal } from 'decimal.js';
import { draftActions } from '../draftReducer';
import { amountFormatter, computeTotalSelectedFees } from './fees.helper';
import { CURRENCY_POSITION_TYPE } from '../../../shared/enums/FeeCellType';
import { useFeesConfiguration } from '../../../redux/features/configuration/useFeesConfiguration';

/**
 * Hook containing logic for the fees selection in the fees table.
 *
 * @param formData - the schema.jsonschema feesSelection object (holding the selected fees and complementary data)
 * @param feesSelection - the uiSchema feesSelection (holding the domain fees data)
 * @param formContext - submission form context
 * @param onBlur - FeesSelectionField's onBlur handler
 * @param onChange  - FeesSelectionField's onChange handler
 * @returns hook with utility methods
 */
export function useFees(formData, feesSelection, formContext, onBlur, onChange, registry) {
  const feesValueReference = useRef([]);

  const feesConfiguration = useFeesConfiguration();

  // This use effect is needed to add the total to the data model
  // mitigating the performance issues of json schema
  useEffect(() => {
    if (!feesValueReference.current || feesValueReference.current.length < 1) {
      feesValueReference.current = feesSelection?.values?.reduce((accumulator, fee) => {
        // eslint-disable-next-line no-param-reassign
        accumulator[fee.code] = fee;
        return accumulator;
      }, {});
    }
  }, []);

  const getFeeAdditionalConfig = code => feesConfiguration.resource?.fees?.find(fee => fee.code === code) || {};

  const getFeeAmount = code => getFeeAdditionalConfig(code).amount;

  const multiplyFeeAmountAndQuantity = (amount, quantity) => {
    if (amount === undefined || amount === null) {
      return amount;
    }
    return new Decimal(amount).mul(quantity).toNumber();
  };

  const generateNewSelectedFee = (fee, quantity) => {
    const feeWithValues = feesValueReference.current[fee.code];
    const feeAdditionalConfig = getFeeAdditionalConfig(fee.code);
    const applicableDiscount = formData?.applicableDiscounts?.find(_fee => _fee.code === fee.code);
    const priceAfterDiscount =
      (applicableDiscount && applicableDiscount.priceWithDiscount) || feeAdditionalConfig.amount;
    const _quantity = quantity !== undefined ? quantity : feeWithValues.quantity || 1;
    return {
      code: feeWithValues.code,
      price: feeAdditionalConfig.amount,
      quantity: _quantity,
      discount: (applicableDiscount && applicableDiscount.discount) || feeWithValues.discount,
      subtotal: multiplyFeeAmountAndQuantity(priceAfterDiscount, _quantity),
      feeType: feeWithValues.feeType,
      category: feeWithValues.category,
    };
  };

  const calculateSubtotal = (feeSelection, feeCode, checked, quantity) => {
    const currency = !feesSelection.hideCurrency ? `${feesSelection?.currencySymbol} ` : '';
    let feeAmount =
      formData?.selectedFees &&
      Object.keys(formData?.selectedFees).length > 0 &&
      formData?.selectedFees?.find(fee => fee.code === feeCode)?.subtotal;
    if (!feeAmount) {
      feeAmount = checked ? multiplyFeeAmountAndQuantity(getFeeAmount(feeCode), quantity) : 0;
    }
    const feeText = `${amountFormatter().format(feeAmount)}`;
    return feeSelection?.currencyPosition === CURRENCY_POSITION_TYPE?.AFTER
      ? `${feeText} ${currency}`
      : `${currency} ${feeText}`;
  };

  const onChangeData = (feeList, currency, currencySymbol) => {
    const total = computeTotalSelectedFees(feeList, feesSelection);
    const currencyPosition = feesSelection?.currencyPosition;
    const applicableDiscounts = formData?.applicableDiscounts;
    const applicableSurcharges = formData?.applicableSurcharges;
    if (registry) {
      registry.triggerPostProcess('fee_selection');
    }
    onChange({
      ...formData,
      selectedFees: feeList,
      total,
      currencyPosition,
      currency,
      currencySymbol,
      applicableDiscounts,
      applicableSurcharges,
    });
  };

  const onClickCheckbox = fee => (event) => {
    let selectedFees = formData?.selectedFees ? [...formData?.selectedFees] : [];
    if (event.target.checked) {
      selectedFees.push(generateNewSelectedFee(fee));
    } else {
      selectedFees = selectedFees?.filter(_fee => _fee.code !== fee.code);
    }
    onChangeData(selectedFees, feesSelection.currency, feesSelection.currencySymbol);
  };

  const onChangeQuantity = fee => (event) => {
    const selectedFees = [...formData?.selectedFees.filter(_fee => _fee.code !== fee.code)];
    selectedFees.push(generateNewSelectedFee(fee, Number(event.target.value)));
    onChangeData(selectedFees, feesSelection.currency, feesSelection.currencySymbol);
  };

  const onChangeAdditionalField = (fee, additionalField) => (event) => {
    const selectedFeeIndex = formData?.selectedFees.findIndex(_fee => _fee.code === fee.code);
    const selectedFee = formData?.selectedFees[selectedFeeIndex];
    selectedFee[additionalField] = Number(event.target.value);

    onChangeData(formData?.selectedFees, feesSelection.currency, feesSelection.currencySymbol, 'fee_selection');
  };

  const onBlurQuantity = fee => () => {
    const selectedFees = formData?.selectedFees ? [...formData?.selectedFees] : [];
    const selectedFee = selectedFees.find(_fee => _fee.code === fee.code);
    if (!selectedFee.quantity && onBlur) {
      const applicableDiscount = formData?.applicableDiscounts?.find(_fee => _fee.code === fee.code);
      const priceAfterDiscount = (applicableDiscount && applicableDiscount.priceWithDiscount) || getFeeAmount(fee.code);
      selectedFee.quantity = 1;
      selectedFee.subtotal = priceAfterDiscount;
      onChangeData(selectedFees, feesSelection.currency, feesSelection.currencySymbol);
    }
  };

  const onBlurCheckbox = (event) => {
    if (onBlur) {
      onBlur(event.target.id);
      if (typeof formContext.dispatchDraftState === 'function') {
        formContext.dispatchDraftState({ type: draftActions.CALCULATE_WORKFLOW_POSTPROCESS_START });
      }
    }
  };

  const onChangeBarcode = fee => (barcode, isValidated = null) => {
    const selectedFee = formData?.selectedFees?.find(_fee => _fee.code === fee.code);
    if (selectedFee) {
      selectedFee.barcode = barcode;
      if (isValidated != null) {
        selectedFee.isValidated = isValidated;
      }
    }
    onChange(formData);
  };

  const onChangeLateFee = fee => () => {
    const selectedFees = formData?.selectedFees ? [...formData?.selectedFees] : [];
    const selectedFee = selectedFees.find(_fee => _fee.code === fee.code);
    selectedFee.isLate = !selectedFee.isLate;
    onChangeData(selectedFees, feesSelection.currency, feesSelection.currencySymbol);
    registry.triggerPostProcess('late_fee_toggle');
  };

  return {
    amountFormatter,
    onClickCheckbox,
    onChangeQuantity,
    onBlurCheckbox,
    onBlurQuantity,
    calculateSubtotal,
    onChangeBarcode,
    getFeeAdditionalConfig,
    onChangeLateFee,
    onChangeAdditionalField,
  };
}
